import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Fab,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ForumIcon from '@material-ui/icons/Forum';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import SendIcon from '@material-ui/icons/Send';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import MicIcon from '@material-ui/icons/Mic';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import { toastInfo } from '../../errors/toastSuccess';

import {
  getChats,
  createChat as createChatAPI,
  deleteChat,
  getChatMessages,
  sendChatMessage,
  deleteChatMessage,
  markChatRead,
} from '../../services/internalChatService';
import { getSenderAvatarUrl } from '../../utils/userAvatar';
import toastError from '../../errors/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';
import { UsersFilter } from '../UsersFilter';
import { useDate } from '../../hooks/useDate';
import { useInternalChatVoiceRecorder } from '../../hooks/useInternalChatVoiceRecorder';
import useFileDropZone from '../../hooks/useFileDropZone';
import { i18n } from '../../translate/i18n';
import {
  getInternalChatDeleteMessagePreview,
  getInternalChatMediaKind,
  stripInternalChatMessageCaption,
} from '../../utils/internalChatMedia';
import {
  getInternalChatUnreadForUser,
  isInternalChatOwner,
  sumInternalChatUnreadsForUser,
  appendInternalChatMessage,
  upsertInternalChatInList,
} from '../../utils/internalChatSync';
import { playInternalChatNotificationSound } from '../../utils/notificationSound';
import { ChatModal } from '../../pages/Chat/index';
import ConfirmationModal from '../ConfirmationModal';
import FileDropOverlay from '../FileDropOverlay';
import InternalChatAttachmentPreviewModal from '../InternalChatAttachmentPreviewModal';
import InternalChatMessageMedia from '../InternalChatMessageMedia';
import InternalChatVoiceNote from '../InternalChatVoiceNote';
import DeleteConfirmModal from '../MessageActionsModal/DeleteConfirmModal';
import { useStyles, useCreateChatDialogStyles } from './styles';

function formatRecordingClock(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  const z = (n) => (n < 10 ? `0${n}` : `${n}`);
  return `${z(m)}:${z(s)}`;
}

export default function InternalChatWidget({
  disabled = false,
  open: openProp,
  onOpenChange,
  onUnreadChange,
}) {
  const classes = useStyles();
  const createDialogClasses = useCreateChatDialogStyles();
  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();

  const theme = useTheme();
  // `noSsr` faz a query resolver já na primeira renderização. Sem isso o valor
  // inicial seria `false` e o FAB apareceria por um frame antes de recolher.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  // No mobile o FAB nasce recolhido: a tela é pequena e ele fica em cima do
  // conteúdo, perto da barra inferior. No desktop segue aparecendo por padrão.
  // Só o valor inicial depende do tamanho — redimensionar a janela não desfaz
  // a escolha de quem já mexeu na abinha.
  const [fabVisible, setFabVisible] = useState(!isMobile);
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp !== undefined ? openProp : openInternal;
  const setOpen = useCallback(
    (value) => {
      const next = typeof value === 'function' ? value(open) : value;
      setOpenInternal(next);
      onOpenChange?.(next);
    },
    [open, onOpenChange]
  );
  const [search, setSearch] = useState('');
  const [chats, setChats] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [_messagesPageInfo, setMessagesPageInfo] = useState({ hasMore: false });
  const [messagesPage, setMessagesPage] = useState(1);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createTitleError, setCreateTitleError] = useState('');
  const [createUsers, setCreateUsers] = useState([]);
  const [pendingAttachmentFile, setPendingAttachmentFile] = useState(null);
  const [attachmentPreviewModalOpen, setAttachmentPreviewModalOpen] = useState(false);
  const [deleteMessageModalOpen, setDeleteMessageModalOpen] = useState(false);
  const [pendingDeleteMessageId, setPendingDeleteMessageId] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [confirmDeleteConversationOpen, setConfirmDeleteConversationOpen] = useState(false);
  const [chatPendingDelete, setChatPendingDelete] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalChat, setEditModalChat] = useState(null);
  const [typingLabel, setTypingLabel] = useState(null);
  const messagesBaseRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const messageScrollTailRef = useRef({ len: 0, lastId: null });
  const currentChatIdRef = useRef(null);
  const widgetFileInputRef = useRef(null);
  const composerInputElRef = useRef(null);
  const shouldRestoreComposerFocusRef = useRef(false);
  const messageInputRef = useRef('');
  const typingTimerRef = useRef(null);
  const lastTypingEmitRef = useRef(0);

  const emitTyping = useCallback(() => {
    if (!socket || !currentChat?.id) return;
    const now = Date.now();
    if (now - lastTypingEmitRef.current < 1500) return;
    lastTypingEmitRef.current = now;
    socket.emit('chatTyping', { chatId: currentChat.id, userId: user.id, userName: user.name });
  }, [socket, currentChat?.id, user.id, user.name]);

  useEffect(() => {
    currentChatIdRef.current = currentChat?.id ?? null;
  }, [currentChat?.id]);

  const postChatMessage = useCallback(
    async (content, file) => {
      if ((!content && !file) || !currentChat?.id || sendingMessage) return;
      shouldRestoreComposerFocusRef.current = true;
      setSendingMessage(true);
      try {
        let created;
        if (file) {
          const fd = new FormData();
          fd.append('message', content);
          fd.append('medias', file, file.name);
          ({ data: created } = await sendChatMessage(currentChat.id, fd));
        } else {
          ({ data: created } = await sendChatMessage(currentChat.id, {
            message: content,
          }));
        }
        const newMessage = created?.newMessage || created?.record || created;
        if (newMessage?.id != null) {
          setMessages((prev) => appendInternalChatMessage(prev, newMessage));
        }
        setMessageInput('');
        setPendingAttachmentFile(null);
        if (widgetFileInputRef.current) widgetFileInputRef.current.value = '';
      } catch (err) {
        toastError(err);
      } finally {
        setSendingMessage(false);
      }
    },
    [currentChat?.id, sendingMessage]
  );

  useEffect(() => {
    messageInputRef.current = messageInput;
  }, [messageInput]);

  const { isRecording, recordingSeconds, startRecording, confirmRecording, cancelRecording } =
    useInternalChatVoiceRecorder({
      onFile: (file) => {
        postChatMessage(messageInputRef.current.trim(), file);
      },
      onError: (err) => {
        if (err?.message === 'unsupported') {
          toastInfo(i18n.t('chatInternal.media.recordUnsupported'));
        } else {
          toast.error(i18n.t('chatInternal.media.recordError'));
        }
      },
    });

  useEffect(() => {
    if (!pendingAttachmentFile) setAttachmentPreviewModalOpen(false);
  }, [pendingAttachmentFile]);

  // Só um arquivo por mensagem no chat interno (o backend grava `medias[0]`).
  const handleDroppedFiles = useCallback((files) => {
    const [file] = files;
    if (!file) return;
    setPendingAttachmentFile(file);
    setAttachmentPreviewModalOpen(true);
  }, []);

  const { isDragging, dropZoneProps } = useFileDropZone({
    onFiles: handleDroppedFiles,
    disabled:
      !currentChat ||
      loadingMessages ||
      sendingMessage ||
      isRecording ||
      attachmentPreviewModalOpen,
  });

  useEffect(() => {
    if (!shouldRestoreComposerFocusRef.current) return;
    if (sendingMessage || loadingMessages) return;
    if (!currentChat?.id) {
      shouldRestoreComposerFocusRef.current = false;
      return;
    }
    requestAnimationFrame(() => composerInputElRef.current?.focus?.());
    shouldRestoreComposerFocusRef.current = false;
  }, [sendingMessage, loadingMessages, currentChat?.id]);

  const handleStartVoice = () => {
    setPendingAttachmentFile(null);
    if (widgetFileInputRef.current) widgetFileInputRef.current.value = '';
    startRecording();
  };

  const sendMessage = () => postChatMessage(messageInput.trim(), pendingAttachmentFile);

  const requestDeleteMessage = useCallback(
    (messageId) => {
      if (!currentChat?.id || messageId == null) return;
      setPendingDeleteMessageId(messageId);
      setDeleteMessageModalOpen(true);
    },
    [currentChat?.id]
  );

  const handleDeleteMessageModalClose = useCallback(() => {
    setDeleteMessageModalOpen(false);
    setPendingDeleteMessageId(null);
  }, []);

  const confirmDeleteMessage = useCallback(async () => {
    const messageId = pendingDeleteMessageId;
    if (!currentChat?.id || messageId == null) {
      setDeleteMessageModalOpen(false);
      setPendingDeleteMessageId(null);
      return;
    }
    if (sendingMessage) return;
    setDeleteMessageModalOpen(false);
    setPendingDeleteMessageId(null);
    try {
      await deleteChatMessage(currentChat.id, messageId);
      setMessages((prev) => prev.filter((m) => String(m.id) !== String(messageId)));
    } catch (err) {
      toastError(err);
    }
  }, [currentChat?.id, pendingDeleteMessageId, sendingMessage]);

  const filteredChats = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return chats;
    return chats.filter((c) =>
      `${c.title || ''} ${c.lastMessage || ''}`.toLowerCase().includes(term)
    );
  }, [search, chats]);

  const unreadsCount = useMemo(
    () => sumInternalChatUnreadsForUser(chats, user?.id),
    [chats, user?.id]
  );

  useEffect(() => {
    onUnreadChange?.(unreadsCount);
  }, [unreadsCount, onUnreadChange]);

  const scrollToBottom = useCallback(() => {
    const el = messagesAreaRef.current;
    if (!el) {
      messagesBaseRef.current?.scrollIntoView({ block: 'end', behavior: 'auto' });
      return;
    }
    const run = () => {
      el.scrollTop = el.scrollHeight;
    };
    run();
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  }, []);

  useLayoutEffect(() => {
    messageScrollTailRef.current = { len: 0, lastId: null };
  }, [currentChat?.id]);

  useEffect(() => {
    setTypingLabel(null);
    clearTimeout(typingTimerRef.current);
  }, [currentChat?.id]);

  useLayoutEffect(() => {
    const list = Array.isArray(messages) ? messages : [];
    const len = list.length;
    const lastId = len > 0 ? list[len - 1]?.id : null;
    const prev = messageScrollTailRef.current;
    const grew = len > prev.len;
    const lastChanged = lastId != null && lastId !== prev.lastId;
    if (grew && lastChanged) {
      scrollToBottom();
    }
    messageScrollTailRef.current = { len, lastId };
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!typingLabel) return;
    requestAnimationFrame(() => scrollToBottom());
  }, [typingLabel, scrollToBottom]);

  // Ao reabrir o widget dentro de um chat já selecionado, mantém a visão nas últimas mensagens.
  useEffect(() => {
    if (!open || !currentChat?.id) return;
    const timers = [];
    requestAnimationFrame(() => scrollToBottom());
    // Reforço em pequenos atrasos: cobre mídia/layout tardio e garante fundo real da conversa.
    [80, 220, 420].forEach((ms) => {
      const id = setTimeout(() => scrollToBottom(), ms);
      timers.push(id);
    });
    return () => {
      timers.forEach((id) => clearTimeout(id));
    };
  }, [open, currentChat?.id, scrollToBottom]);

  const loadChats = async () => {
    try {
      const { data } = await getChats();
      const list = Array.isArray(data?.records) ? data.records : [];
      setChats(list);
      setCurrentChat((cur) => {
        if (!cur?.id) return cur;
        const updated = list.find((c) => String(c.id) === String(cur.id));
        return updated ? { ...updated } : cur;
      });
    } catch (err) {
      toastError(err);
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditModalChat(null);
    loadChats();
  };

  const handleConfirmDeleteConversation = async () => {
    const chat = chatPendingDelete;
    setChatPendingDelete(null);
    if (!chat?.id) return;
    try {
      await deleteChat(chat.id);
      setChats((prev) => prev.filter((c) => String(c.id) !== String(chat.id)));
      if (currentChat?.id && String(currentChat.id) === String(chat.id)) {
        setPendingAttachmentFile(null);
        if (widgetFileInputRef.current) widgetFileInputRef.current.value = '';
        setCurrentChat(null);
        setMessages([]);
        setMessagesPage(1);
        setMessageInput('');
      }
    } catch (err) {
      toastError(err);
    }
  };

  const loadMessages = async (chatId, reset = true) => {
    if (!chatId) return;
    try {
      setLoadingMessages(true);
      const nextPage = reset ? 1 : messagesPage;
      const { data } = await getChatMessages(chatId, { pageNumber: nextPage });
      setMessagesPageInfo(data || { hasMore: false });
      if (reset) {
        setMessages(Array.isArray(data?.records) ? data.records : []);
        setMessagesPage(2);
      } else {
        setMessages((prev) => [...(Array.isArray(data?.records) ? data.records : []), ...prev]);
        setMessagesPage((prev) => prev + 1);
      }
      if (reset) {
        setTimeout(scrollToBottom, 120);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const markRead = async (chat) => {
    if (!chat?.id) return;
    try {
      await markChatRead(chat.id);
      setChats((prev) =>
        prev.map((c) => {
          if (String(c.id) !== String(chat.id) || !Array.isArray(c.users)) return c;
          return {
            ...c,
            users: c.users.map((u) =>
              String(u.userId) === String(user.id) ? { ...u, unreads: 0 } : u
            ),
          };
        })
      );
    } catch (_) {
      // silent
    }
  };

  const selectChat = async (chat) => {
    if (!chat?.id) return;
    setPendingAttachmentFile(null);
    if (widgetFileInputRef.current) widgetFileInputRef.current.value = '';
    setCurrentChat(chat);
    setMessages([]);
    setMessagesPage(1);
    await loadMessages(chat.id, true);
    await markRead(chat);
  };

  const handleBackToList = () => {
    setPendingAttachmentFile(null);
    if (widgetFileInputRef.current) widgetFileInputRef.current.value = '';
    setCurrentChat(null);
    setMessages([]);
    setMessagesPage(1);
    setMessageInput('');
  };

  const createChat = async () => {
    if (!Array.isArray(createUsers) || createUsers.length === 0) return;
    const trimmed = (createTitle || '').trim();
    if (!trimmed) {
      setCreateTitleError(i18n.t('chatInternal.modal.titleRequired'));
      return;
    }
    setCreateTitleError('');
    try {
      const { data } = await createChatAPI({
        title: trimmed,
        users: createUsers,
      });
      setCreateDialogOpen(false);
      setCreateTitle('');
      setCreateTitleError('');
      setCreateUsers([]);
      await loadChats();
      if (data?.id) {
        await selectChat(data);
      }
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    if (open) {
      loadChats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (disabled || !user?.companyId || !socket?.on || !socket?.off) return;

    const companyId = user.companyId;
    const activeChatId = currentChat?.id ?? null;

    const onChatUser = (data) => {
      if (data?.action === 'create' && data?.record) {
        setChats((prev) => upsertInternalChatInList(prev, data.record));
      }
      if (data?.action === 'update' && data?.record) {
        setChats((prev) => upsertInternalChatInList(prev, data.record));
        setCurrentChat((cur) =>
          cur?.id != null && String(cur.id) === String(data.record.id)
            ? { ...cur, ...data.record }
            : cur
        );
      }
    };

    const applyDeleteMessageFromSocket = (data) => {
      if (data?.action !== 'delete-message' || data.messageId == null) return;
      const messageId = data.messageId;
      const chatPayload = data.chat;
      if (chatPayload?.id != null) {
        setChats((prev) => upsertInternalChatInList(prev, chatPayload));
        setCurrentChat((cur) =>
          cur?.id != null && String(cur.id) === String(chatPayload.id) ? { ...chatPayload } : cur
        );
      }
      if (
        currentChatIdRef.current != null &&
        chatPayload?.id != null &&
        String(chatPayload.id) === String(currentChatIdRef.current)
      ) {
        setMessages((prev) => prev.filter((m) => String(m.id) !== String(messageId)));
      }
    };

    const appendMessageIfOpen = (newMessage, chat) => {
      if (!newMessage || currentChatIdRef.current == null) return;
      const chatId = chat?.id ?? newMessage?.chatId ?? currentChatIdRef.current;
      if (String(chatId) !== String(currentChatIdRef.current)) return;
      setMessages((prev) => appendInternalChatMessage(prev, newMessage));
    };

    const onCompanyChat = (data) => {
      if (
        data?.action === 'typing' &&
        data?.userId &&
        String(data.userId) !== String(user.id) &&
        currentChatIdRef.current &&
        String(data.chatId || data?.chat?.id) === String(currentChatIdRef.current)
      ) {
        setTypingLabel(data.userName || 'Alguém');
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setTypingLabel(null), 3000);
        return;
      }
      if (data?.action === 'delete' && data?.id != null) {
        const delId = data.id;
        setChats((prev) => prev.filter((c) => String(c.id) !== String(delId)));
        if (
          currentChatIdRef.current != null &&
          String(currentChatIdRef.current) === String(delId)
        ) {
          setCurrentChat(null);
          setMessages([]);
        }
      }
      if ((data?.action === 'new-message' || data?.action === 'update') && data?.chat) {
        setChats((prev) => upsertInternalChatInList(prev, data.chat));
        setCurrentChat((cur) =>
          cur?.id != null && String(cur.id) === String(data.chat.id)
            ? { ...cur, ...data.chat }
            : cur
        );
        if (
          data.action === 'new-message' &&
          data.newMessage &&
          String(data.newMessage.senderId) !== String(user.id) &&
          String(data.chat.id) !== String(currentChatIdRef.current)
        ) {
          playInternalChatNotificationSound();
        }
      }
      applyDeleteMessageFromSocket(data);
    };

    const onCurrentChat = (data) => {
      if (!currentChatIdRef.current) return;
      if (data?.action === 'typing' && data?.userId && String(data.userId) !== String(user.id)) {
        setTypingLabel(data.userName || 'Alguém');
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setTypingLabel(null), 3000);
        return;
      }
      if (data?.action === 'new-message' && data?.newMessage) {
        clearTimeout(typingTimerRef.current);
        setTypingLabel(null);
        appendMessageIfOpen(data.newMessage, data.chat);
        if (data?.chat) {
          setChats((prev) => upsertInternalChatInList(prev, data.chat));
          setCurrentChat((cur) =>
            String(cur.id) === String(data.chat.id) ? { ...cur, ...data.chat } : cur
          );
        }
        if (String(data.newMessage.senderId) !== String(user.id)) {
          playInternalChatNotificationSound();
        }
        setTimeout(scrollToBottom, 80);
      }
      if (data?.action === 'update' && data?.chat) {
        setChats((prev) => upsertInternalChatInList(prev, data.chat));
        setCurrentChat((cur) =>
          String(cur.id) === String(data.chat.id) ? { ...cur, ...data.chat } : cur
        );
      }
      applyDeleteMessageFromSocket(data);
    };

    socket.on(`company-${companyId}-chat-user-${user.id}`, onChatUser);
    socket.on(`company-${companyId}-chat`, onCompanyChat);
    if (activeChatId) {
      socket.on(`company-${companyId}-chat-${activeChatId}`, onCurrentChat);
    }

    return () => {
      socket.off(`company-${companyId}-chat-user-${user.id}`, onChatUser);
      socket.off(`company-${companyId}-chat`, onCompanyChat);
      if (activeChatId) {
        socket.off(`company-${companyId}-chat-${activeChatId}`, onCurrentChat);
      }
    };
  }, [disabled, socket, user?.companyId, user?.id, currentChat?.id, scrollToBottom]);

  const pendingDeletePreview = useMemo(() => {
    const m = messages.find((x) => String(x.id) === String(pendingDeleteMessageId));
    if (!m) return '';
    return getInternalChatDeleteMessagePreview(m, {
      audioLabel: i18n.t('chatInternal.deleteModal.previewAudio'),
      imageLabel: i18n.t('chatInternal.deleteModal.previewImage'),
      videoLabel: i18n.t('chatInternal.deleteModal.previewVideo'),
      documentLabel: i18n.t('chatInternal.deleteModal.previewDocument'),
    });
  }, [messages, pendingDeleteMessageId]);

  const threadSubtitle = useMemo(() => {
    if (!currentChat) return '';
    const plain = stripInternalChatMessageCaption(currentChat.lastMessage || '').replace(
      /\s+/g,
      ' '
    );
    const t = plain.trim();
    if (t.length > 88) return `${t.slice(0, 88)}…`;
    return t;
  }, [currentChat]);

  if (disabled) return null;

  return (
    <>
      <DeleteConfirmModal
        open={deleteMessageModalOpen}
        onClose={handleDeleteMessageModalClose}
        onConfirm={confirmDeleteMessage}
        messageText={pendingDeletePreview}
      />
      <ConfirmationModal
        title="Excluir conversa"
        open={confirmDeleteConversationOpen}
        onClose={() => setConfirmDeleteConversationOpen(false)}
        onConfirm={handleConfirmDeleteConversation}
      >
        Esta ação não pode ser revertida. Confirmar exclusão?
      </ConfirmationModal>
      {editModalOpen && editModalChat ? (
        <ChatModal
          open={editModalOpen}
          chat={editModalChat}
          type="edit"
          handleClose={closeEditModal}
          handleLoadNewChat={() => {}}
        />
      ) : null}
      <InternalChatAttachmentPreviewModal
        open={attachmentPreviewModalOpen}
        file={pendingAttachmentFile}
        onDiscard={() => {
          setPendingAttachmentFile(null);
          setAttachmentPreviewModalOpen(false);
          if (widgetFileInputRef.current) widgetFileInputRef.current.value = '';
        }}
        onSend={() => {
          sendMessage();
        }}
        sendDisabled={!currentChat || loadingMessages || sendingMessage}
      />
      {/* Aba lateral: mostra/esconde o FAB — posicionada próxima a ele.
          No mobile o painel vai quase de borda a borda (`right: 10`) e passaria
          por baixo da aba; enquanto ele estiver aberto a aba sai de cena. */}
      {!(isMobile && open) && (
        <Box
          className={classes.chatToggleTab}
          onClick={() => setFabVisible((v) => !v)}
          role="button"
          tabIndex={0}
          aria-label={fabVisible ? 'Ocultar botão de chat' : 'Mostrar botão de chat'}
          onKeyDown={(e) => e.key === 'Enter' && setFabVisible((v) => !v)}
        >
          <ForumIcon className={classes.chatToggleTabIcon} />
          {fabVisible ? (
            <ChevronRightIcon className={classes.chatToggleTabChevron} />
          ) : (
            <ChevronLeftIcon className={classes.chatToggleTabChevron} />
          )}
        </Box>
      )}

      {/* FAB — desliza para fora quando oculto */}
      <Fab
        color="primary"
        className={`${classes.fab} ${open ? classes.fabOpen : classes.fabClosed} ${!fabVisible ? classes.fabHidden : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-label={
          open ? i18n.t('chatInternal.modal.cancel') : i18n.t('chatInternal.widget.hubKicker')
        }
      >
        {!open && unreadsCount > 0 ? (
          <Box
            component="span"
            className={classes.fabUnreadBadge}
            aria-label={String(unreadsCount)}
          >
            {unreadsCount > 99 ? '99+' : unreadsCount}
          </Box>
        ) : null}
        {open ? <CloseIcon /> : <ForumIcon />}
      </Fab>

      <Backdrop
        className={classes.widgetBackdrop}
        open={open}
        onClick={() => setOpen(false)}
        aria-hidden
      />

      {open && (
        <Box className={classes.panelRoot} {...dropZoneProps}>
          <FileDropOverlay
            visible={isDragging}
            subtitle={i18n.t('messageInput.dropzone.subtitleSingle')}
          />
          {!currentChat ? (
            <>
              <div className={classes.hubHeader}>
                <div className={classes.hubHeaderTop}>
                  <div className={classes.hubKicker}>
                    <span className={classes.statusDot} aria-hidden />
                    {i18n.t('chatInternal.widget.hubKicker')}
                  </div>
                  <div className={classes.hubHeaderActions}>
                    <Tooltip title={i18n.t('chatInternal.modal.cancel')}>
                      <IconButton
                        size="small"
                        className={classes.iconOnHeader}
                        onClick={() => setOpen(false)}
                        aria-label={i18n.t('chatInternal.modal.cancel')}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>
              <Box className={classes.searchRow}>
                <TextField
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={i18n.t('chatInternal.widget.searchPlaceholder')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{ notched: false }}
                  className={classes.searchField}
                />
              </Box>
              <div className={classes.listArea}>
                {filteredChats.map((chat) => {
                  const unread = getInternalChatUnreadForUser(chat, user?.id);
                  const preview =
                    stripInternalChatMessageCaption(chat.lastMessage || '').trim() ||
                    datetimeToClient(chat.updatedAt);
                  const isOwner = isInternalChatOwner(chat, user?.id);
                  return (
                    <Box key={chat.id} className={classes.chatRowOuter}>
                      <Box
                        component="button"
                        type="button"
                        className={classes.chatRow}
                        onClick={() => selectChat(chat)}
                      >
                        <Box
                          className={`${classes.chatRowAvatar} ${
                            unread > 0 ? classes.chatRowAvatarUnread : ''
                          }`}
                          aria-hidden
                        >
                          {unread > 0 ? (
                            <span className={classes.chatRowAvatarCount}>
                              {unread > 99 ? '99+' : unread}
                            </span>
                          ) : (
                            <ForumIcon />
                          )}
                        </Box>
                        <Box className={classes.chatRowBody}>
                          <Typography className={classes.chatRowTitle}>
                            {chat.title || `Chat ${chat.id}`}
                          </Typography>
                          <Typography className={classes.chatRowPreview}>{preview}</Typography>
                        </Box>
                        <Box className={classes.chatRowMeta}>
                          <Typography className={classes.chatRowDate}>
                            {datetimeToClient(chat.updatedAt)}
                          </Typography>
                        </Box>
                      </Box>
                      {isOwner ? (
                        <div
                          className={classes.chatRowActions}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          role="presentation"
                        >
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              className={classes.chatRowActionBtn}
                              aria-label="Editar conversa"
                              onClick={() => {
                                setEditModalChat(chat);
                                setEditModalOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              className={`${classes.chatRowActionBtn} ${classes.chatRowActionBtnDanger}`}
                              aria-label="Excluir conversa"
                              onClick={() => {
                                setChatPendingDelete(chat);
                                setConfirmDeleteConversationOpen(true);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      ) : null}
                    </Box>
                  );
                })}
                {filteredChats.length === 0 ? (
                  <div className={classes.emptyState}>
                    {i18n.t('chatInternal.widget.emptyList')}
                  </div>
                ) : null}
              </div>
              <Box className={classes.newChatCtaWrap}>
                <Button
                  fullWidth
                  color="primary"
                  variant="outlined"
                  className={classes.newChatCta}
                  startIcon={<AddIcon style={{ fontSize: 20 }} />}
                  onClick={() => {
                    setCreateTitleError('');
                    setCreateDialogOpen(true);
                  }}
                >
                  {i18n.t('chatInternal.widget.newConversation')}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <div className={classes.threadHeader}>
                <Tooltip title={i18n.t('chatInternal.header.back')}>
                  <IconButton
                    size="small"
                    className={classes.iconOnHeader}
                    onClick={handleBackToList}
                    aria-label={i18n.t('chatInternal.header.back')}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Box className={classes.threadHeaderText}>
                  <Typography className={classes.threadTitle}>
                    {currentChat.title || `Chat ${currentChat.id}`}
                  </Typography>
                  <Typography className={classes.threadSubtitle}>
                    {threadSubtitle ||
                      (!loadingMessages && messages.length === 0
                        ? i18n.t('chatInternal.widget.emptyThread')
                        : '')}
                  </Typography>
                </Box>
                <Tooltip title={i18n.t('chatInternal.modal.cancel')}>
                  <IconButton
                    size="small"
                    className={classes.iconOnHeader}
                    onClick={() => setOpen(false)}
                    aria-label={i18n.t('chatInternal.modal.cancel')}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
              <div
                ref={messagesAreaRef}
                className={`${classes.messagesArea} ${typingLabel ? classes.messagesAreaTyping : ''}`}
              >
                {messages.length === 0 && !loadingMessages ? (
                  <div className={classes.emptyState}>
                    {i18n.t('chatInternal.widget.emptyThread')}
                  </div>
                ) : null}
                {messages.map((item, index) => {
                  const mine = item?.senderId === user?.id;
                  const html = DOMPurify.sanitize(item?.message || '');
                  const plainCaption = stripInternalChatMessageCaption(html);
                  const rawMedia = item?.mediaPath || item?.mediaUrl;
                  const hasMedia = Boolean(rawMedia);
                  const mediaKind = hasMedia
                    ? getInternalChatMediaKind(rawMedia, item.mediaName)
                    : null;
                  const isAudio = mediaKind === 'audio';
                  const hasNonAudioMedia = hasMedia && !isAudio;
                  const hasCaption = Boolean(plainCaption);
                  const audioOnly = isAudio && !hasCaption && !hasNonAudioMedia;
                  const showBubble =
                    hasNonAudioMedia ||
                    (!hasMedia && hasCaption) ||
                    (hasMedia && hasCaption) ||
                    (!mine && !audioOnly);

                  const senderName = item?.sender?.name || 'Usuário';
                  const senderInitial = senderName[0]?.toUpperCase() || '?';
                  const senderAvatarSrc = getSenderAvatarUrl(item?.sender) || undefined;

                  return (
                    <Box
                      key={`${item.id || index}-${item.createdAt}`}
                      className={`${classes.messageRow} ${mine ? classes.messageRowMine : classes.messageRowTheirs}`}
                    >
                      {!mine && (
                        <Avatar className={classes.senderAvatar} src={senderAvatarSrc}>
                          {senderInitial}
                        </Avatar>
                      )}
                      <Box
                        className={`${classes.messageStack} ${
                          mine ? classes.messageStackMine : classes.messageStackTheirs
                        }`}
                      >
                        {!mine && audioOnly && (item?.sender?.name || 'Usuário') ? (
                          <Typography component="span" className={classes.senderAboveVoice}>
                            {item?.sender?.name || 'Usuário'}
                          </Typography>
                        ) : null}
                        {showBubble ? (
                          <Box className={mine ? classes.messageRight : classes.messageLeft}>
                            {!mine && !audioOnly ? (
                              <Typography component="span" className={classes.messageSenderName}>
                                {item?.sender?.name || 'Usuário'}
                              </Typography>
                            ) : null}
                            {hasMedia && plainCaption ? (
                              <Typography component="span" className={classes.messageCaptionPlain}>
                                {plainCaption}
                              </Typography>
                            ) : null}
                            {!hasMedia && plainCaption ? (
                              <div
                                className={classes.messageBody}
                                dangerouslySetInnerHTML={{ __html: html }}
                              />
                            ) : null}
                            {hasNonAudioMedia ? (
                              <InternalChatMessageMedia
                                mediaPath={rawMedia}
                                mediaName={item.mediaName}
                                bubbleTone={mine ? 'outgoing' : 'incoming'}
                              />
                            ) : null}
                            {!isAudio ? (
                              <Box className={classes.messageTimeRow}>
                                <Typography component="span" className={classes.messageTime}>
                                  {datetimeToClient(item?.createdAt)}
                                </Typography>
                                {mine && item.id != null ? (
                                  <Tooltip title={i18n.t('chatInternal.deleteMessage')}>
                                    <IconButton
                                      size="small"
                                      className={classes.messageDeleteIcon}
                                      aria-label={i18n.t('chatInternal.deleteMessage')}
                                      onClick={() => requestDeleteMessage(item.id)}
                                      disabled={sendingMessage}
                                    >
                                      <DeleteOutlineIcon style={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                              </Box>
                            ) : null}
                          </Box>
                        ) : null}
                        {isAudio ? (
                          <InternalChatVoiceNote
                            mediaPath={rawMedia}
                            mediaName={item.mediaName}
                            speakerName={mine ? user?.name : item?.sender?.name}
                            mine={mine}
                            sender={item?.sender}
                          />
                        ) : null}
                        {isAudio ? (
                          <Box
                            component="div"
                            className={`${classes.timeBelowVoice} ${
                              mine ? classes.timeBelowVoiceMine : classes.timeBelowVoiceTheirs
                            }`}
                          >
                            <Typography
                              component="span"
                              variant="inherit"
                              style={{ fontSize: 'inherit', color: 'inherit' }}
                            >
                              {datetimeToClient(item?.createdAt)}
                            </Typography>
                            {mine && item.id != null ? (
                              <Tooltip title={i18n.t('chatInternal.deleteMessage')}>
                                <IconButton
                                  size="small"
                                  className={classes.messageDeleteIcon}
                                  aria-label={i18n.t('chatInternal.deleteMessage')}
                                  onClick={() => requestDeleteMessage(item.id)}
                                  disabled={sendingMessage}
                                >
                                  <DeleteOutlineIcon style={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}
                <div ref={messagesBaseRef} />
              </div>

              <div className={classes.composer}>
                {typingLabel ? (
                  <div className={classes.typingBubbleFloating}>
                    <div
                      className={classes.typingBubble}
                      aria-live="polite"
                      aria-label={`${typingLabel} está digitando`}
                    >
                      <span className={classes.typingDot} />
                      <span className={`${classes.typingDot} ${classes.typingDot2}`} />
                      <span className={`${classes.typingDot} ${classes.typingDot3}`} />
                    </div>
                  </div>
                ) : null}
                <input
                  ref={widgetFileInputRef}
                  type="file"
                  hidden
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.7z"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setPendingAttachmentFile(f);
                      setAttachmentPreviewModalOpen(true);
                    }
                  }}
                />
                <div className={classes.composerRow}>
                  {!isRecording ? (
                    <Tooltip title={i18n.t('chatInternal.media.attach')}>
                      <IconButton
                        size="small"
                        onClick={() => widgetFileInputRef.current?.click()}
                        disabled={!currentChat || loadingMessages || sendingMessage}
                        aria-label={i18n.t('chatInternal.media.attach')}
                        className={classes.actionIconMuted}
                      >
                        <AttachFileIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  <Box className={classes.composerInputShell}>
                    {isRecording ? (
                      <TextField
                        value=""
                        placeholder={i18n.t('chatInternal.media.recordingPlaceholder')}
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled
                        className={classes.composerTextField}
                        InputProps={{ readOnly: true, notched: false }}
                      />
                    ) : (
                      <TextField
                        value={messageInput}
                        onChange={(e) => {
                          setMessageInput(e.target.value);
                          emitTyping();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                        placeholder={i18n.t('chatInternal.widget.typeMessage')}
                        variant="outlined"
                        size="small"
                        multiline
                        minRows={1}
                        maxRows={6}
                        fullWidth
                        disabled={!currentChat}
                        className={classes.composerTextField}
                        inputRef={composerInputElRef}
                        InputProps={{ notched: false }}
                      />
                    )}
                  </Box>
                  {isRecording ? (
                    <Box className={classes.recordingChronoOutside} aria-live="polite">
                      <span className={classes.recordingDot} aria-hidden />
                      <Typography component="span" className={classes.recordingTime}>
                        {formatRecordingClock(recordingSeconds)}
                      </Typography>
                    </Box>
                  ) : null}
                  <Box className={classes.recorderActions}>
                    {isRecording ? (
                      <>
                        <Tooltip title={i18n.t('chatInternal.media.recordCancel')}>
                          <IconButton
                            size="small"
                            onClick={cancelRecording}
                            disabled={!currentChat || loadingMessages || sendingMessage}
                            aria-label={i18n.t('chatInternal.media.recordCancel')}
                          >
                            <HighlightOffIcon
                              className={classes.cancelRecordIcon}
                              fontSize="small"
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={i18n.t('chatInternal.media.recordConfirm')}>
                          <IconButton
                            size="small"
                            onClick={confirmRecording}
                            disabled={!currentChat || loadingMessages || sendingMessage}
                            aria-label={i18n.t('chatInternal.media.recordConfirm')}
                          >
                            <CheckCircleOutlineIcon
                              className={classes.confirmRecordIcon}
                              fontSize="small"
                            />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : messageInput.trim() || pendingAttachmentFile ? (
                      <Tooltip title={i18n.t('chatInternal.media.send')}>
                        <span className={classes.composerIconWrap}>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={sendMessage}
                            disabled={
                              !currentChat ||
                              sendingMessage ||
                              loadingMessages ||
                              (!messageInput.trim() && !pendingAttachmentFile) ||
                              attachmentPreviewModalOpen
                            }
                            aria-label={i18n.t('chatInternal.media.send')}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={i18n.t('chatInternal.media.recordAudio')}>
                        <span className={classes.composerIconWrap}>
                          <IconButton
                            size="small"
                            onClick={handleStartVoice}
                            disabled={!currentChat || loadingMessages || sendingMessage}
                            aria-label={i18n.t('chatInternal.media.recordAudio')}
                            className={classes.actionIconMuted}
                          >
                            <MicIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </div>
              </div>
            </>
          )}
        </Box>
      )}

      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateTitle('');
          setCreateTitleError('');
          setCreateUsers([]);
          setCreateDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        aria-labelledby="widget-create-chat-title"
        PaperProps={{ className: createDialogClasses.dialogPaper }}
      >
        <Box className={createDialogClasses.dialogHeader} component="header">
          <Box className={createDialogClasses.headerLeft}>
            <Box className={createDialogClasses.headerIcon} aria-hidden>
              <ForumIcon />
            </Box>
            <Box minWidth={0}>
              <Typography
                component="h2"
                className={createDialogClasses.headerTitle}
                id="widget-create-chat-title"
              >
                Novo chat interno
              </Typography>
              <Typography className={createDialogClasses.headerSubtitle}>
                Defina um título e escolha os participantes do chat.
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Fechar">
            <IconButton
              size="small"
              onClick={() => {
                setCreateTitle('');
                setCreateTitleError('');
                setCreateUsers([]);
                setCreateDialogOpen(false);
              }}
              className={createDialogClasses.headerCloseBtn}
              aria-label="Fechar"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <DialogContent className={createDialogClasses.dialogContent}>
          <Box className={createDialogClasses.sectionCard}>
            <Typography component="h3" className={createDialogClasses.sectionTitle}>
              Dados da conversa
            </Typography>
            <TextField
              value={createTitle}
              onChange={(e) => {
                setCreateTitle(e.target.value);
                if (createTitleError) setCreateTitleError('');
              }}
              label="Título do chat"
              placeholder="Ex.: Financeiro e suporte"
              variant="outlined"
              size="small"
              fullWidth
              className={createDialogClasses.field}
              error={Boolean(createTitleError)}
              helperText={createTitleError || undefined}
            />
            <UsersFilter onFiltered={setCreateUsers} includeBots />
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ display: 'block', marginTop: 8 }}
            >
              Selecione ao menos um participante para criar o chat.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions className={createDialogClasses.dialogActions}>
          <Button
            className={createDialogClasses.btn}
            onClick={() => {
              setCreateTitle('');
              setCreateTitleError('');
              setCreateUsers([]);
              setCreateDialogOpen(false);
            }}
          >
            Cancelar
          </Button>
          <Button
            className={createDialogClasses.btn}
            onClick={createChat}
            color="primary"
            variant="contained"
            disabled={!Array.isArray(createUsers) || createUsers.length === 0}
          >
            Criar chat
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
